import { useState, useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { governanceApi, workspacesApi, BrandVoice } from '../../services/api'
import { matchesStored } from './storedValue'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardList,
  Chip,
  ChipGroup,
  Eyebrow,
  Field,
  FieldLabel,
  FieldRow,
  Hint,
  IconButton,
  LoadingState,
  SaveBar,
  SectionLayout,
  Slider,
  Stack,
  TileGrid,
  Transcript,
} from '../../ds'

/**
 * How we sound — everything the merchant STATES about her voice, under the review card that
 * shows what she derived (Brand tab, MAR-384; v5 app IA §3.6, §7).
 *
 * ── One surface, one Save ───────────────────────────────────────────────────────────────
 *
 * v6 merges three cards into this one: the voice form, the assistant name (which used to be
 * `AssistantNameCard`) and the preview. The sticky bar saves BOTH writes the surface holds:
 *
 * - the brand voice, through the governance API (`PUT …/governance/brand-voice`), which a
 *   member may call; and
 * - the assistant name, through `PUT /workspaces/{id}`, which since MAR-170 is owner/admin only.
 *
 * ⚠️ So the name is EDITABLE only when the tab says so (`nameEditable`, from the same role gate
 * `RestrictedTab` uses). A member sees the name read-only and is never offered a Save that would
 * quietly drop it — the bar's dirty state counts only the fields this viewer may write.
 *
 * ⚠️ `assistantName` still has exactly ONE writer in the app, and this is it. The resolved
 * persona name pins the outbound sign-off (`agent-context/blocks/identity.js`), which is what
 * stops the agent inventing a human at the bottom of an email; two writers would be two
 * surfaces that can disagree about what she is called.
 *
 * The two writes are independent, so they can land apart. Each is reported for what it did —
 * "your voice saved, the name did not" — never as one success or one failure.
 *
 * ── The tone controls ───────────────────────────────────────────────────────────────────
 *
 * Where a reading exists, the review card above IS the tone control (Correct her), so the
 * preset grid here is folded away behind "Set them yourself" (`traitsOpen`). It is always open
 * when nothing usable was read ("Set it myself instead") and whenever the STORED tone is
 * `custom` — the merchant's own words are never hidden behind a click, and never dropped: the
 * payload still carries every trait whether the grid is open or not, exactly as before.
 */

interface BrandVoiceSectionProps {
  /** Called after Cancel has put the form back. */
  onClose?: () => void
  /**
   * A voice save landed, and it moved the voice every consumer reads.
   *
   * ⚠️ The review card above this form renders the EFFECTIVE voice — the merchant's own value
   * wherever `fieldSources` marks one, the derivation's otherwise — under the line "This is
   * what she uses now". A save here stamps that marker and moves that value, so without this
   * call back the card keeps showing the superseded one until a reload. Not called for a
   * name-only save: the name is not part of the voice the card shows.
   */
  onSaved?: () => void
  /** The Brand tab's section nav. Given one, this lays itself out with "Hear it" as the aside. */
  nav?: ReactNode
  /** What sits above the form in the main column — the section heading and the review card. */
  lead?: ReactNode
  /** Show the tone / formality / emoji / length controls unfolded. @default true */
  traitsOpen?: boolean
  /** May this viewer write the assistant name (owner/admin)? @default false */
  nameEditable?: boolean
  /**
   * Bumped when the review card above confirms or corrects (a COUNTER: two confirmations
   * re-seed twice). Those acts move the four traits on the server, so the form re-reads and
   * re-seeds THOSE — tone (with its description), formality, emoji, length — and nothing else.
   *
   * ⚠️ Never a remount. The form below the card holds unsaved edits the confirmation knows
   * nothing about — words to avoid, phrases, examples, the assistant name — and a remount
   * threw every one of them away (PR #324 review). Leaving the traits stale is the other
   * failure: Save would write the pre-confirmation tone straight back, stamping merchant
   * markers on four traits the merchant just agreed to.
   */
  traitsVersion?: number
}

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Formal and business-appropriate' },
  { value: 'casual', label: 'Casual', description: 'Relaxed and conversational' },
  { value: 'playful', label: 'Playful', description: 'Fun and energetic' },
  { value: 'luxurious', label: 'Luxurious', description: 'Elegant and sophisticated' },
  { value: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
  { value: 'custom', label: 'Custom', description: 'Define your own tone' },
] as const

const FORMALITY_LABELS = ['Very casual', 'Casual', 'Balanced', 'Formal', 'Very formal']

// Illustrative bubble shown before a live preview is generated.
const SAMPLE_LINES: Record<string, string> = {
  professional:
    '"I\'m unable to apply a 20% reduction here. It is, however, an excellent match for your requirements — and returns are complimentary should the fit prove unsuitable."',
  casual: '"Can\'t quite do 20% on this one, but honestly it looks like just your thing — and returns are free if it\'s not right."',
  playful: '"20%\'s a touch beyond me today! But I think this one\'s a keeper — and returns are on us if it\'s not love at first wear."',
  luxurious:
    '"A 20% reduction isn\'t possible on this piece — though I suspect it\'s precisely the fit you\'ve been seeking. Returns, of course, are entirely complimentary."',
  friendly: '"I can\'t stretch to 20% on this one, but I\'ve a feeling it\'s the fit you\'re after — so here\'s free returns if it isn\'t quite right."',
  custom: '"I can\'t go to 20% here, but it\'s a strong match for what you\'re after — and returns are free if the fit\'s off."',
}

/** What the form shows where nothing (or nothing readable) is stored. */
const DEFAULT_FORM = {
  tone: 'friendly' as (typeof TONE_OPTIONS)[number]['value'],
  customToneDescription: '',
  formalityLevel: 3,
  forbiddenWords: [] as string[],
  requiredPhrases: { greeting: '', signOff: '', apology: '', thankYou: '' },
  exampleResponses: [] as Array<{ context: string; response: string }>,
  emojiUsage: 'sparingly' as 'never' | 'sparingly' | 'frequently',
  responseLength: 'balanced' as 'concise' | 'balanced' | 'detailed',
}

/**
 * The fields this form writes — `BRAND_VOICE_FIELDS` in `@marva/shared/schemas`, which is also
 * the set the PUT SETs or REMOVEs. The GET's row carries more than these (`workspaceId`,
 * `configType`, `fieldSources`, `derived`, `updatedAt`), none of which this form writes, so the
 * dirty check compares the payload against the stored row's projection onto exactly these keys.
 */
const BRAND_VOICE_FIELDS = [
  'tone',
  'customToneDescription',
  'formalityLevel',
  'forbiddenWords',
  'requiredPhrases',
  'exampleResponses',
  'emojiUsage',
  'responseLength',
] as const

/** The form as seeded from a stored row. */
const formFromRow = (brandVoice: BrandVoice): typeof DEFAULT_FORM => ({
  tone: (brandVoice.tone || 'friendly') as typeof DEFAULT_FORM.tone,
  // Read back, not blanked: seeding this empty meant the textarea opened blank on a
  // workspace that HAS a custom tone, and the next save then wrote that blank over
  // the merchant's own words.
  customToneDescription: brandVoice.customToneDescription || '',
  formalityLevel: brandVoice.formalityLevel || 3,
  forbiddenWords: brandVoice.forbiddenWords || [],
  requiredPhrases: {
    greeting: brandVoice.requiredPhrases?.greeting || '',
    signOff: brandVoice.requiredPhrases?.signOff || '',
    apology: '',
    thankYou: '',
  },
  exampleResponses: brandVoice.exampleResponses || [],
  emojiUsage: (brandVoice.emojiUsage || 'sparingly') as typeof DEFAULT_FORM.emojiUsage,
  responseLength: (brandVoice.responseLength || 'balanced') as typeof DEFAULT_FORM.responseLength,
})

/** The fields a confirmation or correction in the review card moves — and nothing else. */
const traitsOf = (form: typeof DEFAULT_FORM) => ({
  tone: form.tone,
  customToneDescription: form.customToneDescription,
  formalityLevel: form.formalityLevel,
  emojiUsage: form.emojiUsage,
  responseLength: form.responseLength,
})

const storedVoiceOf = (raw: unknown): Record<string, unknown> | null => {
  if (raw === null || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  return BRAND_VOICE_FIELDS.reduce<Record<string, unknown>>((acc, k) => {
    if (row[k] !== undefined) acc[k] = row[k]
    return acc
  }, {})
}

/**
 * How the last read of the stored voice ended. `loading` is never stored: it is what any
 * workspace without a settled read of its own reads as (see `readStatus` below).
 *
 * ⚠️ `empty` and `failed` must never collapse. `empty` is a fact — nothing is stored, so the
 * defaults on screen are something to save (D-50). `failed` is an unknown — the merchant may
 * well have a voice stored, and a Save from defaults would overwrite it.
 */
type ReadStatus = 'loading' | 'ok' | 'empty' | 'failed'

// The workspace record is loosely typed at this boundary; `assistantName` is not on the
// shared `Workspace` interface yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WS = any

/** The outcome line under the bar — both halves said, never one verdict for two writes. */
interface Outcome {
  tone: 'success' | 'danger'
  text: string
}

const BrandVoiceSection = ({ onClose, onSaved, nav, lead, traitsOpen = true, nameEditable = false, traitsVersion = 0 }: BrandVoiceSectionProps) => {
  const { currentWorkspace } = useWorkspaceStore()
  const [read, setRead] = useState<{ workspaceId: string; status: Exclude<ReadStatus, 'loading'> } | null>(null)
  // Bumped by "Try again" to re-run the read for the same workspace.
  const [readAttempt, setReadAttempt] = useState(0)
  // Scoped at the read: a settled read belongs only to the workspace that produced it.
  const readStatus: ReadStatus =
    read && read.workspaceId === currentWorkspace?.id ? read.status : 'loading'
  const [formData, setFormData] = useState(DEFAULT_FORM)
  /** What the form was seeded with (or last saved as) — what Cancel puts back. */
  const [baseline, setBaseline] = useState(DEFAULT_FORM)
  /**
   * The voice as STORED — the row as read, or as last successfully saved — projected onto the
   * fields this form writes. `null` when nothing was read (no row, or the read failed — which
   * `readStatus` tells apart; only the former makes the defaults dirty).
   *
   * D-50: the form is dirty when what Save would write (`brandVoicePayload`) differs from THIS,
   * never from what the form displays. So defaults drawn for a missing voice are something to
   * save, and an edit Save would drop (a description under a preset tone) is not an edit.
   */
  const [stored, setStored] = useState<Record<string, unknown> | null>(null)
  // The workspace on screen NOW (set by the load effect), so a save that resolves after a
  // switch lands nothing under B.
  const workspaceIdRef = useRef(currentWorkspace?.id)
  const [isUpdating, setIsUpdating] = useState(false)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [newForbiddenWord, setNewForbiddenWord] = useState('')
  const [previewScenario, setPreviewScenario] = useState('A customer is asking about product availability')
  const [previewResponse, setPreviewResponse] = useState('')
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [traitsUnfolded, setTraitsUnfolded] = useState(false)

  // ── The assistant name. Scoped at the READ, like every cached value here (PR #103): a draft
  // stamped with another workspace is simply not current, so a switch cannot paint A's typed
  // name under B for the frame before an effect runs.
  const savedName = (((currentWorkspace as WS)?.assistantName ?? '') as string)
  const [nameDraft, setNameDraft] = useState<{ workspaceId: string; value: string } | null>(null)
  const assistantName = nameDraft && nameDraft.workspaceId === currentWorkspace?.id ? nameDraft.value : savedName
  // ⚠️ A viewer who may not write the name never has a dirty one — that is what keeps a member
  // from being offered a Save that would silently drop it.
  const nameDirty = nameEditable && assistantName.trim() !== savedName.trim()

  useEffect(() => {
    const loadBrandVoice = async () => {
      if (!currentWorkspace?.id) return

      // ⚠️ Reset FIRST, so nothing of the previous workspace's voice survives into this one. An
      // EMPTY read then shows the defaults as unsaved (D-50); a FAILED one shows them under an
      // alert with Save blocked — never as the merchant's settings, never as an edit to save.
      const asked = currentWorkspace.id
      workspaceIdRef.current = asked
      setRead(null)
      setFormData(DEFAULT_FORM)
      setBaseline(DEFAULT_FORM)
      setStored(null)
      setOutcome(null)
      setIsUpdating(false)
      setPreviewResponse('')
      try {
        const result = await governanceApi.getBrandVoice(asked)
        if (cancelled) return

        // ⚠️ A refusal RESOLVES (`apiClient.request` returns `{ success: false }`, it never
        // rejects), so this branch — not the catch — is what a 500, a 403 or a network failure
        // reaches. It is an unknown, never "nothing stored".
        if (!result.success) {
          setRead({ workspaceId: asked, status: 'failed' })
          return
        }
        if (!result.data) {
          setRead({ workspaceId: asked, status: 'empty' })
          return
        }
        const brandVoice = result.data
        const loaded = formFromRow(brandVoice)
        setFormData(loaded)
        setBaseline(loaded)
        setStored(storedVoiceOf(brandVoice))
        setRead({ workspaceId: asked, status: 'ok' })
      } catch (error) {
        if (cancelled) return
        console.error('Failed to load brand voice:', error)
        setRead({ workspaceId: asked, status: 'failed' })
      }
    }

    let cancelled = false
    loadBrandVoice()
    return () => {
      cancelled = true
    }
  }, [currentWorkspace?.id, readAttempt])

  /**
   * The card above confirmed or corrected: re-read, and re-seed the four traits ONLY (see
   * `traitsVersion`). Every other field keeps what the merchant typed, and stays dirty against
   * the new `stored` exactly as before. The baseline (what Cancel restores) becomes the server's.
   *
   * ⚠️ Until that re-read settles, the traits on screen are the PRE-confirmation ones, and a Save
   * would stamp them `setBy: 'merchant'` over what was just agreed. So the re-seed is a state of
   * its own, derived at the READ (`traitsVersion !== seededVersion`, no effect in between), and
   * it blocks Save the same render the prop moves (PR #324 review 2).
   *
   * ⚠️ A failed re-seed is NOT a failed first read. Its Try again repeats the re-seed — merging
   * the four traits only — and never re-runs the first load, which resets the whole form to
   * defaults and would wipe the words, phrases and examples the merchant has not saved. Only a
   * workspace change resets the form (and that is a remount: the tab keys this on the workspace).
   */
  const [seededVersion, setSeededVersion] = useState(traitsVersion)
  const [reseedFailedFor, setReseedFailedFor] = useState<number | null>(null)
  // Bumped by a failed re-seed's Try again.
  const [reseedAttempt, setReseedAttempt] = useState(0)
  const reseedFailed = reseedFailedFor === traitsVersion && traitsVersion !== seededVersion
  const reseeding = traitsVersion !== seededVersion && !reseedFailed
  useEffect(() => {
    if (traitsVersion === seededVersion) return
    const asked = currentWorkspace?.id
    if (!asked) return
    const version = traitsVersion
    let cancelled = false
    setReseedFailedFor(null)
    const reseed = async () => {
      try {
        const result = await governanceApi.getBrandVoice(asked)
        if (cancelled || workspaceIdRef.current !== asked) return
        if (!result.success || !result.data) {
          setReseedFailedFor(version)
          return
        }
        const fresh = formFromRow(result.data)
        setFormData((prev) => ({ ...prev, ...traitsOf(fresh) }))
        setBaseline(fresh)
        setStored(storedVoiceOf(result.data))
        setRead({ workspaceId: asked, status: 'ok' })
        setSeededVersion(version)
      } catch {
        if (!cancelled && workspaceIdRef.current === asked) setReseedFailedFor(version)
      }
    }
    void reseed()
    return () => {
      cancelled = true
    }
    // `seededVersion` is read, not a trigger: a settled re-seed must not start another.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traitsVersion, reseedAttempt, currentWorkspace?.id])

  /**
   * The ONE place this form becomes a `BrandVoice` payload.
   *
   * ⚠️ There were two, and only one of them knew about `customToneDescription`. Save sent it;
   * preview built its own object and dropped it — and `buildBrandVoicePrompt` evaluates a custom
   * tone as `config.customToneDescription || 'balanced'`, so pressing Generate on a custom voice
   * demonstrated a BALANCED one. A second builder is how that happened, so there is one.
   */
  const brandVoicePayload = (): BrandVoice => ({
    tone: formData.tone as BrandVoice['tone'],
    // ⚠️ Carried only with `tone: 'custom'`. The PUT REMOVEs it whenever it is absent, which is
    // what clears prose the merchant walked away from — and `tone: 'custom'` with nothing behind
    // it is a voice neither party chose.
    ...(formData.tone === 'custom' && formData.customToneDescription.trim()
      ? { customToneDescription: formData.customToneDescription.trim() }
      : {}),
    formalityLevel: formData.formalityLevel,
    emojiUsage: formData.emojiUsage as BrandVoice['emojiUsage'],
    responseLength: formData.responseLength as BrandVoice['responseLength'],
    forbiddenWords: formData.forbiddenWords,
    requiredPhrases: formData.requiredPhrases,
    exampleResponses: formData.exampleResponses,
  })

  const readFailed = readStatus === 'failed'
  // D-50: dirty when what Save would write differs from what is stored (`storedValue.ts`).
  // A failed read leaves `stored` null, but that is not "nothing stored": the defaults on screen
  // are not the merchant's settings and are not an edit. Save is blocked instead (below).
  const voiceDirty = !readFailed && readStatus !== 'loading' && !matchesStored(brandVoicePayload(), stored)
  /** Save may not run: the stored voice is unknown, or the traits on screen are behind it. */
  const saveBlocked = readFailed || reseeding || reseedFailed

  /** Saves what is dirty, both writes at once, and reports each for what it did. */
  const handleSave = async () => {
    if (!currentWorkspace) return
    // Only after a settled read: from `failed` (or mid-read) the form holds defaults, and a
    // Save would write them over a voice this screen could not see.
    if (readStatus !== 'ok' && readStatus !== 'empty') return
    // Nor while the traits on screen are behind a confirmation (see `reseeding`).
    if (reseeding || reseedFailed) return
    if (!voiceDirty && !nameDirty) return

    const savedFor = currentWorkspace.id
    const payload = brandVoicePayload()
    const formSent = formData
    const name = assistantName.trim()
    const failure = (e: unknown, fallback: string) => ({ ok: false as const, error: e instanceof Error ? e.message : fallback })

    setIsUpdating(true)
    setOutcome(null)
    const [voiceResult, nameResult] = await Promise.all([
      voiceDirty
        ? governanceApi.saveBrandVoice(savedFor, payload)
          .then((r) => (r.success ? { ok: true as const } : { ok: false as const, error: r.error || 'That did not save.' }))
          .catch((e) => failure(e, 'That did not save.'))
        : null,
      nameDirty
        ? workspacesApi.update(savedFor, { assistantName: name } as never)
          .then((r) => (r.success ? { ok: true as const } : { ok: false as const, error: r.error || 'That did not save.' }))
          .catch((e) => failure(e, 'That did not save.'))
        : null,
    ])
    if (workspaceIdRef.current !== savedFor) return
    setIsUpdating(false)

    if (voiceResult?.ok) {
      // What is stored is now what was SENT, so an edit made during the request stays dirty.
      setStored(storedVoiceOf(payload))
      setBaseline(formSent)
      // ⚠️ Only after a SUCCESSFUL write. A refused save moved nothing.
      onSaved?.()
    }
    if (nameResult?.ok) {
      // ⚠️ Read the store at CALL time: a value closed over before the await goes stale
      // across a workspace switch, and would reinstate the old workspace object with it.
      const { currentWorkspace: live, setCurrentWorkspace } = useWorkspaceStore.getState()
      if (live && live.id === savedFor) {
        setCurrentWorkspace({ ...live, assistantName: name } as never)
        setNameDraft({ workspaceId: savedFor, value: name })
      }
    }

    const failed = [
      voiceResult && !voiceResult.ok ? `your brand voice did not save (${voiceResult.error})` : null,
      nameResult && !nameResult.ok ? `the name did not save (${nameResult.error})` : null,
    ].filter(Boolean) as string[]
    if (failed.length === 0) {
      setOutcome({ tone: 'success', text: 'Saved. She uses this from her next message.' })
    } else {
      const saved = [voiceResult?.ok ? 'Your brand voice saved' : null, nameResult?.ok ? 'The name saved' : null].filter(Boolean)
      const lead = saved.length ? `${saved.join(' and ')}, but ` : ''
      const text = `${lead}${failed.join(' and ')}.`
      setOutcome({ tone: 'danger', text: text[0].toUpperCase() + text.slice(1) })
    }
  }

  const handleCancel = () => {
    setFormData(baseline)
    setNameDraft(null)
    setOutcome(null)
    onClose?.()
  }

  const addForbiddenWord = () => {
    const word = newForbiddenWord.trim()
    if (word && !formData.forbiddenWords.includes(word)) {
      setFormData((prev) => ({ ...prev, forbiddenWords: [...prev.forbiddenWords, word] }))
      setNewForbiddenWord('')
    }
  }

  const removeForbiddenWord = (word: string) => {
    setFormData((prev) => ({ ...prev, forbiddenWords: prev.forbiddenWords.filter((w) => w !== word) }))
  }

  const addExampleResponse = () => {
    if (formData.exampleResponses.length < 5) {
      setFormData((prev) => ({ ...prev, exampleResponses: [...prev.exampleResponses, { context: '', response: '' }] }))
    }
  }

  const updateExampleResponse = (index: number, field: 'context' | 'response', value: string) => {
    setFormData((prev) => ({
      ...prev,
      exampleResponses: prev.exampleResponses.map((ex, i) => (i === index ? { ...ex, [field]: value } : ex)),
    }))
  }

  const removeExampleResponse = (index: number) => {
    setFormData((prev) => ({ ...prev, exampleResponses: prev.exampleResponses.filter((_, i) => i !== index) }))
  }

  const generatePreview = async () => {
    if (!currentWorkspace) return

    setIsGeneratingPreview(true)
    setPreviewResponse('')

    try {
      const result = await governanceApi.previewBrandVoice(currentWorkspace.id, {
        scenario: previewScenario,
        // The same payload the Save button sends — see `brandVoicePayload`. A preview built from
        // a different object is a preview of a different voice.
        brandVoice: brandVoicePayload(),
      })

      if (result.success && result.data) {
        setPreviewResponse(result.data.response)
      } else {
        setPreviewResponse('Error generating preview. Please try again.')
      }
    } catch {
      setPreviewResponse('Error generating preview. Please try again.')
    } finally {
      setIsGeneratingPreview(false)
    }
  }

  const frame = (main: ReactNode, aside: ReactNode) =>
    nav ? (
      <SectionLayout nav={nav} aside={aside} asideLabel="Hear it" asideWidth="wide">
        <Stack>{lead}{main}</Stack>
      </SectionLayout>
    ) : (
      <Stack>{lead}{main}{aside}</Stack>
    )

  if (!currentWorkspace?.id) return null

  if (readStatus === 'loading') {
    return frame(<LoadingState caption="Loading brand voice settings…" />, null)
  }

  const bubble = previewResponse || SAMPLE_LINES[formData.tone] || SAMPLE_LINES.friendly
  const dirty = voiceDirty || nameDirty
  const traitsVisible = traitsOpen || traitsUnfolded || stored?.tone === 'custom'

  const nameControl = nameEditable ? (
    <Field
      label="Assistant name"
      value={assistantName}
      onChange={(e) => setNameDraft({ workspaceId: currentWorkspace.id, value: (e.target as HTMLInputElement).value })}
      placeholder="Marva"
      maxLength={50}
      // ⚠️ Not "and SMS". `resolveSenderPersona` has exactly two consumers — `shopper-email` and
      // `agent-conversation` — and neither SMS service references it, so a text never carries
      // the persona. Add SMS here only when an SMS path resolves the persona.
      hint={<>The name she signs off with on email and chat. Leave blank to use <b>Marva</b>.</>}
    />
  ) : (
    <Stack gap="sm">
      <FieldLabel as="p">Assistant name</FieldLabel>
      <CardBody>
        She signs off as <b>{savedName.trim() || 'Marva'}</b>{savedName.trim() ? '' : ' — no name is set'}. Only a
        workspace owner or admin can change the name.
      </CardBody>
    </Stack>
  )

  const main = (
    <Stack>
      {readFailed && (
        <Alert
          tone="danger"
          live="assertive"
          actions={
            <Button size="sm" variant="ghost" onClick={() => setReadAttempt((n) => n + 1)}>
              Try again
            </Button>
          }
        >
          She couldn’t read your brand voice just now — nothing is lost. Try again before saving: the settings
          below may not be yours.
        </Alert>
      )}
      {reseedFailed && (
        <Alert
          tone="danger"
          live="assertive"
          actions={
            <Button size="sm" variant="ghost" onClick={() => setReseedAttempt((n) => n + 1)}>
              Try again
            </Button>
          }
        >
          She couldn’t catch the form up with the voice you just confirmed. Everything you typed is still here —
          try again before saving, or the tone below would overwrite what you confirmed.
        </Alert>
      )}

      <Card>
        <CardHeader title="What only you can tell her" description="She never derives these." />
        <CardList>
          {nameControl}

          <FieldRow>
            <Field
              label="Forbidden words / phrases"
              value={newForbiddenWord}
              placeholder="Add words she should never use…"
              onChange={(e) => setNewForbiddenWord((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addForbiddenWord()
                }
              }}
            />
            <Button onClick={addForbiddenWord}>Add</Button>
          </FieldRow>
          {formData.forbiddenWords.length > 0 && (
            <ChipGroup label="Forbidden words / phrases">
              {formData.forbiddenWords.map((word) => (
                <Chip key={word} kind="remove" removeLabel={`remove ${word}`} onRemove={() => removeForbiddenWord(word)}>
                  {word}
                </Chip>
              ))}
            </ChipGroup>
          )}

          <Stack gap="sm">
            <FieldLabel as="p">Required phrases</FieldLabel>
            <Hint>Optional. Pin the exact wording she uses for common moments.</Hint>
            <TileGrid>
              <Field
                label="Greeting style"
                value={formData.requiredPhrases.greeting}
                placeholder="e.g. Hi there!"
                onChange={(e) => {
                  const greeting = (e.target as HTMLInputElement).value
                  setFormData((prev) => ({ ...prev, requiredPhrases: { ...prev.requiredPhrases, greeting } }))
                }}
              />
              <Field
                label="Sign-off style"
                value={formData.requiredPhrases.signOff}
                placeholder="e.g. Best, the team"
                onChange={(e) => {
                  const signOff = (e.target as HTMLInputElement).value
                  setFormData((prev) => ({ ...prev, requiredPhrases: { ...prev.requiredPhrases, signOff } }))
                }}
              />
            </TileGrid>
          </Stack>

          <Stack gap="sm">
            <FieldLabel as="p">Example responses</FieldLabel>
            <Hint>Up to 5. Show her how you&apos;d reply in specific situations.</Hint>
            {formData.exampleResponses.map((ex, index) => (
              <Card key={index} tone="recessed" padding="sm">
                <CardHeader
                  title={`Example ${index + 1}`}
                  titleAs="h4"
                  aside={
                    <IconButton
                      size="sm"
                      icon={<X size={14} />}
                      label={`remove example ${index + 1}`}
                      onClick={() => removeExampleResponse(index)}
                    />
                  }
                />
                <CardList>
                  <Field
                    aria-label={`Example ${index + 1} context`}
                    value={ex.context}
                    placeholder="Context: e.g. customer asking about shipping times"
                    onChange={(e) => updateExampleResponse(index, 'context', (e.target as HTMLInputElement).value)}
                  />
                  <Field
                    as="textarea"
                    rows={3}
                    aria-label={`Example ${index + 1} ideal response`}
                    value={ex.response}
                    placeholder="Ideal response: e.g. Great question! We typically ship within 24 hours…"
                    onChange={(e) => updateExampleResponse(index, 'response', (e.target as HTMLTextAreaElement).value)}
                  />
                </CardList>
              </Card>
            ))}
            {formData.exampleResponses.length < 5 && (
              <div>
                <Button variant="ghost" size="sm" onClick={addExampleResponse}>
                  + Add example
                </Button>
              </div>
            )}
          </Stack>
        </CardList>
      </Card>

      {traitsVisible ? (
        <Card>
          <CardHeader
            title="Tone, in your words"
            description="The overall register, set by you. Saving records these four as yours, and she keeps them over any later reading."
          />
          <CardList>
            <TonePicker value={formData.tone} onChange={(tone) => setFormData((prev) => ({ ...prev, tone }))} />

            {formData.tone === 'custom' && (
              <Field
                as="textarea"
                label="Custom tone description"
                rows={3}
                value={formData.customToneDescription}
                maxLength={500}
                placeholder="Describe your desired tone… e.g. 'Warm and knowledgeable, like a trusted friend who happens to be an expert'"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, customToneDescription: (e.target as HTMLTextAreaElement).value }))
                }
              />
            )}

            <Slider
              label="Formality level"
              min={1}
              max={5}
              step={1}
              value={formData.formalityLevel}
              format={(v) => FORMALITY_LABELS[v - 1]}
              minLabel="Like texting a friend"
              maxLabel="Like a business letter"
              onChange={(v) => setFormData((prev) => ({ ...prev, formalityLevel: v }))}
            />

            <TileGrid>
              <Field
                as="select"
                label="Emoji usage"
                value={formData.emojiUsage}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    emojiUsage: (e.target as HTMLSelectElement).value as typeof prev.emojiUsage,
                  }))
                }
              >
                <option value="never">Never</option>
                <option value="sparingly">Use sparingly (1–2 per message)</option>
                <option value="frequently">Freely</option>
              </Field>
              <Field
                as="select"
                label="Response length"
                value={formData.responseLength}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    responseLength: (e.target as HTMLSelectElement).value as typeof prev.responseLength,
                  }))
                }
              >
                <option value="concise">Brief (1 sentence)</option>
                <option value="balanced">Balanced (2–4 sentences)</option>
                <option value="detailed">Detailed (full paragraphs)</option>
              </Field>
            </TileGrid>
          </CardList>
        </Card>
      ) : (
        <Card>
          <CardHeader
            title="Tone, in your words"
            description="She uses the tone, formality, emoji and length above — confirm or correct her there. To write them out yourself instead, including a tone in your own words, set them here. Any Save on this page records these four as yours, folded or not."
            aside={
              <Button variant="ghost" size="sm" onClick={() => setTraitsUnfolded(true)}>
                Set them yourself
              </Button>
            }
          />
        </Card>
      )}

      <SaveBar
        placement="sticky"
        state={isUpdating ? 'saving' : outcome?.tone === 'danger' && dirty ? 'error' : dirty ? 'dirty' : outcome?.tone === 'success' ? 'saved' : 'clean'}
        error={outcome?.tone === 'danger' ? outcome.text : undefined}
        note="Changes apply from her next message."
        blocked={saveBlocked}
        blockedNote={
          readFailed
            ? 'Read your brand voice first — saving now could replace it with settings that are not yours.'
            : reseedFailed
              ? 'Catch up with the voice you confirmed first — try again above.'
              : 'Catching up with the voice you just confirmed…'
        }
        saveLabel="Save"
        onSave={handleSave}
        onCancel={handleCancel}
      />
      {/* The bar says "Saved" or carries the failure; this says the whole sentence, both halves. */}
      {outcome && (
        <Alert tone={outcome.tone} live={outcome.tone === 'danger' ? 'assertive' : 'polite'}>
          {outcome.text}
        </Alert>
      )}
    </Stack>
  )

  const hearIt = (
    <Card tone="recessed">
      <CardList>
        <Eyebrow>
          Hear it · <span>{formData.tone === 'custom' ? 'your own' : formData.tone}</span> voice
        </Eyebrow>
        <Transcript label="Voice preview" turns={[{ from: 'marva', text: bubble }]} />
        <FieldRow>
          <Field
            aria-label="Preview scenario"
            value={previewScenario}
            placeholder="Describe a scenario…"
            onChange={(e) => setPreviewScenario((e.target as HTMLInputElement).value)}
          />
          <Button variant="ghost" onClick={generatePreview} busy={isGeneratingPreview} busyLabel="Generating…">
            Generate
          </Button>
        </FieldRow>
        <Hint>Uses the values on screen, saved or not. Nothing is sent.</Hint>
      </CardList>
    </Card>
  )

  return frame(main, hearIt)
}

type ToneValue = (typeof TONE_OPTIONS)[number]['value']

/**
 * The tone presets as choice cards (`.tone` → `Card interactive role="radio"`): one radiogroup,
 * one tab stop (the checked card), Arrow keys move and select, Tab leaves.
 */
function TonePicker({ value, onChange }: { value: ToneValue; onChange: (tone: ToneValue) => void }) {
  const current = Math.max(0, TONE_OPTIONS.findIndex((o) => o.value === value))

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    e.preventDefault()
    const next = (current + step + TONE_OPTIONS.length) % TONE_OPTIONS.length
    onChange(TONE_OPTIONS[next].value)
    // Card is not a ref-forwarding component, so focus is moved through the group's own DOM.
    e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')[next]?.focus()
  }

  return (
    <TileGrid role="radiogroup" aria-label="Tone preset" onKeyDown={onKeyDown}>
      {TONE_OPTIONS.map((option, i) => {
        const checked = value === option.value
        return (
          <Card
            key={option.value}
            interactive
            padding="sm"
            role="radio"
            aria-checked={checked}
            tabIndex={i === current ? 0 : -1}
            onClick={() => onChange(option.value)}
          >
            <CardHeader inline title={option.label} description={option.description} />
          </Card>
        )
      })}
    </TileGrid>
  )
}

export default BrandVoiceSection
